import PropTypes from 'prop-types'
import { Component } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import compose from 'recompose/compose'
import branch from 'recompose/branch'
import renderNothing from 'recompose/renderNothing'
import DocumentTitle from 'react-document-title'
import DocMeta from 'react-doc-meta'
import IdleTimer from 'react-idle-timer'
import { setIdentityInfo } from '@driftt/web-auth'

import { makeHasGate, isOnFreePlan } from 'app/modules/gates/selectors'
import * as PresenceActions from 'app/modules/presence/actions'
import * as PresenceSelectors from 'app/modules/presence/selectors'
import * as UserSelectors from 'app/modules/user/selectors'
import { actions as TrackingActions } from 'app/modules/tracking'
import * as PropertiesSelectors from 'app/modules/user/properties/selectors'
import * as ExperimentSelectors from 'app/modules/experiment/selectors'
import { org, orgId, properties } from 'app/modules/org/selectors'
import IdleTimeout from 'components/IdleTimeout'
import { Div } from '@driftt/tide-core-legacy'
import { merge } from 'ramda'

import { FullStory } from 'react-fullstory-component'
import GlobalModal from 'components/GlobalModal'
import NotificationBar from 'components/NotificationBar'
import GlobalNotificationBar from 'components/GlobalNotificationBar'
import MainNav from 'components/MainNav'

import { APP_TITLE, HAS_INTEGRATED_HELP, FREE_USER_INTEGRATED_HELP_INTERACTION_ID, PAID_USER_INTEGRATED_HELP_INTERACTION_ID } from 'constants/ResourcesConstants'
import shouldRecordSession, { FullStorySettings } from 'utils/SessionRecording'
import { GLOBAL_SEARCH_KEY, GlobalSearch } from 'components/GlobalSearch'

import Launcher from "@driftt/integrated-help-launcher"

import 'react-dates/lib/css/_datepicker.css'

const DEFAULT_PRESENCE_INTERVAL = 1000

const BLACKLISTED_INTEGRATED_HELP_URLS = Object.freeze([
  "/inboxes",
  "/welcome",
  "/getstarted",
])

class ApplicationComponent extends Component {
  static childContextTypes = {
    location: PropTypes.object,
  }

  static contextTypes = {
    router: PropTypes.object.isRequired,
    location: PropTypes.object,
    app: PropTypes.object,
  }

  getChildContext() {
    return { location: this.props.location }
  }

  componentDidMount() {
    this.parseInts(this.props.params)
    this.parseInts(this.props.query)
    this.trackPage(this.props.location.pathname)
    if (this.props.hasPresence) {
      this.props.actions.setPresenceIsReceiving({ on: true })
      this.props.actions.setCustomInterval(DEFAULT_PRESENCE_INTERVAL)
    }
    window.localStorage.setItem('openTabs', JSON.stringify([]))
    setTimeout(this.onActive, 10000)
  }

  componentWillUnmount() {
    if (this.props.hasPresence) {
      this.props.actions.setPresenceIsReceiving({ on: false })
    }
  }

  componentWillReceiveProps(nextProps) {
    this.parseInts(nextProps.params)
    this.parseInts(nextProps.query)
    this.trackPage(nextProps.location.pathname)
    if (nextProps.org && nextProps.orgProperties){
      setIdentityInfo(nextProps.currentUser, merge({ ... nextProps.orgProperties }, { name: nextProps.org.name }))
    }
  }

  trackPage(pathname) {
    if (pathname === this.lastTrackedPath) {
      return
    }
    this.lastTrackedPath = pathname
    return this.props.actions.page()
  }

  parseInts(params) {
    //this is a marshal thing.
    //https://github.com/Driftt/customer-web/commit/9a767861f6fdb40b65133b492d51440d8c7d71fd
    return _.each(params, function(param, key) {
      if (!isNaN(param)) {
        return (params[key] = parseInt(param))
      }
    })
  }

  getMetaTags() {
    return [
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
      },
    ]
  }

  closeSearch = () => {
    const query = _.cloneDeep(this.props.location.query)
    delete query[GLOBAL_SEARCH_KEY]
    return this.context.router.push({ pathname: this.props.location.pathname, query })
  }

  onActive = () => {
    if (this.props.currentUser) {
      this.props.actions.sendActiveStatus({ isActive: true })
    }
  }

  onIdle = () => {
    if (this.props.currentUser) {
      this.props.actions.sendActiveStatus({ isActive: false })
    }
  }

  renderIntegratedHelp = () => {
    const { isFreeUser, hasIntegratedHelpGate, location } = this.props
    if (!HAS_INTEGRATED_HELP && !hasIntegratedHelpGate) {
      return null
    }

    if (BLACKLISTED_INTEGRATED_HELP_URLS.some(blacklistUrl => location.pathname.startsWith(blacklistUrl))) {
      return null
    }
    const interactionId = isFreeUser ? FREE_USER_INTEGRATED_HELP_INTERACTION_ID : PAID_USER_INTEGRATED_HELP_INTERACTION_ID

    return <Launcher interactionId={interactionId}/>
  }

  render() {
    const { currentUser } = this.props
    
    const AppCore = (
      <Div>
        <IdleTimer onActive={this.onActive} onIdle={this.onIdle} timeout={20000}/>
        <DocMeta tags={this.getMetaTags()}>
          <DocumentTitle title={APP_TITLE}>
            <div className="AppContainer">
              <GlobalNotificationBar currentPath={this.props.location.pathname} />
              <div className="App">
                {this.props.location.query[GLOBAL_SEARCH_KEY] !== undefined && (
                  <GlobalSearch onClose={this.closeSearch} query={this.props.location.query} />
                )}
                <MainNav {...this.props} />
                <div className="app-content-container">
                  <NotificationBar />
                  <div className="app-route-content">
                    {React.cloneElement(this.props.children, { gates: currentUser.gates.toJS() })}
                  </div>
                </div>
                { this.renderIntegratedHelp() }
              </div>
              <GlobalModal />
              {shouldRecordSession(currentUser.gates) && (
                <FullStory
                  settings={FullStorySettings}
                  sessionId={currentUser.email}
                  data={{ ...currentUser.toJS(), displayName: currentUser.name }}
                />
              )}
            </div>
          </DocumentTitle>
        </DocMeta>  
      </Div>
      
    )

    return (
      <IdleTimeout sessionLengthMillis={this.props.sessionLengthMillis}>
        { AppCore }
      </IdleTimeout>
    )
  }
}

const mapStateToProps = state => {
  return {
    hasPresence: makeHasGate('has_presence')(state),
    activeStatus: PresenceSelectors.getActiveStatus(state),
    currentUser: UserSelectors.currentUser(state),
    hasIntegratedHelpGate: makeHasGate('has_integrated_help_active')(state),
    splitLoaded: ExperimentSelectors.getLoaded(state),
    isFreeUser: isOnFreePlan(state),
    orgId: orgId(state),
    org: org(state),
    orgProperties: properties(state),
    videoSignUpDone: PropertiesSelectors.videoSignUpDone(state),
    isDesktopUser: PropertiesSelectors.isDesktopUser(state),
    sessionLengthMillis: properties(state).sessionLengthMillis,
  }
}

const mapDispatchToProps = dispatch => {
  return {
    actions: bindActionCreators(
      {
        sendActiveStatus: PresenceActions.sendActiveStatus,
        setPresenceIsReceiving: PresenceActions.setPresenceIsReceiving,
        setCustomInterval: PresenceActions.setCustomInterval,
        setProcessAllUsers: PresenceActions.setProcessAllUsers,
        page: TrackingActions.page,
      },
      dispatch
    ),
  }
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  branch(props => !props.currentUser, renderNothing),
  branch(props => !props.splitLoaded, renderNothing),
)(ApplicationComponent)



// WEBPACK FOOTER //
// ./src/scripts/components/ApplicationComponent.js